import { LICENSE_MODULE } from "@selector/shared";
import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import type { FormatOptionLabelMeta } from "react-select";
import { type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";

import { simpleFieldValidation } from "@/app-utils/form.ts";
import {
  type Configuration,
  type Database,
  type GenomeSearch,
  type InputUserGroupInput,
  type Organism,
  type SampleType,
  type User,
  type UserGroup,
} from "@/graphql.ts";
import { isLicensed } from "@/redux/modules/config.ts";
import { loadSampleTypes } from "@/redux/modules/listing.ts";
import { type IIdentifiable } from "@/redux/modules/table-data.ts";
import { createNewUsergroup, searchGenomes, validateUsergroupNameIsNotUsed } from "@/redux/modules/user-group-admin.ts";
import { loadAllSessions } from "@/redux/modules/users-admin.ts";
import { type IAppState } from "@/redux/reducer.ts";

import { Dialog } from "../core/overlays/Dialogs.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";

interface ICreateUsergroupDialogOwnProps {
  selectedGenomes?: any;
  onApply?: () => void;
  onCancel?: () => void;
}

interface ICreateUsergroupDialogAppStateProps {
  currentUser?: User;
  users?: User[];
  isPublicProject?: boolean;
  sampleTypes: SampleType[];
  databases: Database[];
  configuration: Configuration;
}

interface ICreateUsergroupDialogDispatchProps {
  getUserList: () => void;
  createUsergroup: (payload: InputUserGroupInput) => Promise<UserGroup>;
  onSearch: (searchTerm: string) => Promise<GenomeSearch>; // callback invoked when the user types in the genomes field
  loadSampleTypes: () => void;
}

export interface ICreateUsergroupDialogProps
  extends ICreateUsergroupDialogAppStateProps,
    ICreateUsergroupDialogDispatchProps,
    ICreateUsergroupDialogOwnProps,
    InjectedFormProps<any, any> {}

function mapStateToProps(state: IAppState): ICreateUsergroupDialogAppStateProps {
  return {
    users: state.usersAdmin.users || [],
    currentUser: state.auth.session,
    sampleTypes: state.listing.sampleTypes || [],
    configuration: state.config.configuration,
  };
}

function mapDispatchToProps(dispatch): ICreateUsergroupDialogDispatchProps {
  return {
    getUserList: () => dispatch(loadAllSessions()),
    createUsergroup: (payload: InputUserGroupInput) => dispatch(createNewUsergroup(payload)),
    onSearch: (searchTerm: string) => dispatch(searchGenomes(searchTerm)),
    loadSampleTypes: () => dispatch(loadSampleTypes()),
  };
}

interface ICreateUsergroupState {
  selectedGenomes?: Organism[];
}

class CreateUsergroupDialogImpl extends Component<ICreateUsergroupDialogProps, ICreateUsergroupState> {
  public static USERGROUP_DIALOG_FIELD_NAME = "Name";
  public static USERGROUP_DIALOG_FIELD_DESCRIPTION = "Description";
  public static USERGROUP_DIALOG_FIELD_MEMBERS = "Members";
  public static USERGROUP_DIALOG_FIELD_GENOMES = "Genomes";
  public static USERGROUP_DIALOG_FIELD_SAMPLE_TYPES = "Sample types";

  constructor(props: ICreateUsergroupDialogProps) {
    super(props);
    this.loadOptions = this.loadOptions.bind(this);
  }
  componentDidMount() {
    this.props.getUserList();
    this.props.loadSampleTypes();
  }

  handleSubmitData = (values): Promise<UserGroup> => {
    const payload: InputUserGroupInput = {
      name: values[CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_NAME].trim(),
      description: values[CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_DESCRIPTION]?.trim(),
      sessionIds: values[CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_MEMBERS]?.map((x) => x.id),
      organismIds: values[CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_GENOMES]?.map((x) => x.id),
      sampleTypeIds: values[CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_SAMPLE_TYPES]?.map((x) => x.id),
      userGroupId: null,
    };
    return this.props.createUsergroup(payload).then(
      (fulfilled) => {
        this.props.onApply();
        this.props.reset();
        return fulfilled;
      },
      // catched by redux form and propagated to this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  render(): ReactElement {
    return (
      <Dialog
        headerProps={{ title: "New User Group" }}
        mainActionButtonProps={{
          children: "Create User Group",
          isDisabled: this.props.submitting,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        footerMessage={{ message: this.props.error }}
        closeFn={() => {
          this.props.reset();
          this.props.onCancel();
        }}
      >
        <div>
          <InputReduxFormField
            tooltip="Provide a name for your new user group"
            componentClass="input"
            name={CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_NAME}
            label="Name"
            placeholder="Enter user group name"
            maxLength={DB_FIELD_MAX_LENGTH.USERGROUP.NAME}
            component={InputField}
            validate={simpleFieldValidation}
          />

          <InputReduxFormField
            tooltip="Provide some optional text to describe this user group"
            componentClass="textarea"
            name={CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_DESCRIPTION}
            label="Description"
            placeholder="Optional description"
            maxLength={DB_FIELD_MAX_LENGTH.USERGROUP.DESCRIPTION}
            component={InputField}
          />

          <ReactSelectReduxFormField
            tooltip={`Optionally, add ${
              this.props.configuration ? this.props.configuration.application.name : ""
            } users to this user group now or modify later. These users will be able to view the genomes of this user group.`}
            name={CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_MEMBERS}
            label="Users"
            placeholder="Optionally, add users to this user group..."
            className="users-selector"
            options={this.props.users}
            component={ReactSelectField}
            isMulti={true}
            isSearchable={true}
            openMenuOnFocus={true}
            isClearable={false}
            closeMenuOnSelect={false}
            getOptionValue={(option: User) => option.id}
            getOptionLabel={(option: User) => option.username}
            formatOptionLabel={(option: User, labelMeta: FormatOptionLabelMeta<User>) =>
              CreateUsergroupDialogImpl.selectOptionFormatter(option, "username", null, labelMeta)
            }
            components={{ IndicatorsContainer: () => null }}
            maxMenuHeight={200}
          />

          <ReactSelectReduxFormField
            name={CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_GENOMES}
            component={ReactSelectField}
            key="selectGenomes"
            label="Genomes"
            placeholder="Optionally, add genomes to this user group..."
            className="genomes-selector"
            noOptionsMessage={() => "No matching genome..."}
            async={true}
            isMulti={true}
            openMenuOnFocus={true}
            isSearchable={true}
            closeMenuOnSelect={false}
            isClearable={false}
            getOptionValue={(option: Organism) => option.id}
            getOptionLabel={(option: Organism) => option.species.name}
            loadOptions={this.loadOptions}
            formatOptionLabel={(option: Organism, labelMeta: FormatOptionLabelMeta<Organism>) =>
              CreateUsergroupDialogImpl.selectOptionFormatter(option, "name", "species.name", labelMeta)
            }
            components={{ IndicatorsContainer: () => null }}
            maxMenuHeight={200}
          />

          {isLicensed(this.props.configuration, LICENSE_MODULE.TRACKER) && (
            <ReactSelectReduxFormField
              tooltip={`Optionally, add sample types to this user group now or modify later. `}
              name={CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_SAMPLE_TYPES}
              label="Sample types"
              placeholder="Optionally, add sample types to this user group..."
              className="genomes-selector"
              options={this.props.sampleTypes}
              component={ReactSelectField}
              isMulti={true}
              isSearchable={true}
              openMenuOnFocus={true}
              isClearable={false}
              closeMenuOnSelect={false}
              getOptionValue={(option: SampleType) => option.id}
              getOptionLabel={(option: SampleType) => option.name}
              formatOptionLabel={(option: User, labelMeta: FormatOptionLabelMeta<User>) =>
                CreateUsergroupDialogImpl.selectOptionFormatter(option, "name", null, labelMeta)
              }
              components={{ IndicatorsContainer: () => null }}
              maxMenuHeight={200}
            />
          )}
        </div>
      </Dialog>
    );
  }

  loadOptions = (searchTerm: string) => {
    return this.props.onSearch(searchTerm).then((genomeSearch: GenomeSearch) => {
      const genomes: Organism[] = genomeSearch.genomes.slice(0);
      return genomes;
    });
  };

  static selectOptionFormatter(
    option: any,
    field1: string,
    field2: string,
    labelMeta: FormatOptionLabelMeta<IIdentifiable>,
  ): ReactElement {
    const isSelection: boolean = labelMeta.context === "value";
    let value1 = option;
    field1.split(".").forEach((x) => (value1 = value1[x]));
    let value2 = "";
    if (field2 !== null) {
      value2 = option;
      field2.split(".").forEach((x) => (value2 = value2[x]));
    }
    const title: string = field2 === null ? `${value1}` : `${value1} (${value2})`;
    return isSelection ? (
      <div key="select-selection">
        <div
          className="main-label"
          style={{ fontWeight: "bold", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 100 }}
          title={title}
        >
          {value1}
        </div>
      </div>
    ) : (
      <div key={"select-option-" + option.id} title={title}>
        <span className="main-label">{value1}</span> {value2 && <span className="secondary">({value2})</span>}
      </div>
    );
  }

  static async asyncValidate(values, dispatch) {
    if (!values) {
      return;
    }

    let usergroupName: string = values[CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_NAME];

    usergroupName = usergroupName && usergroupName.trim();

    if (!usergroupName) {
      throw {
        [CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_NAME]: "A name for the user group is required.",
      };
    } else {
      try {
        if (await dispatch(validateUsergroupNameIsNotUsed(usergroupName, -1))) {
          throw new Error(`A user group with name '${usergroupName}' already exists`);
        }
      } catch (e) {
        throw {
          [CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_NAME]: e.message,
        };
      }
    }
  }
}

const CreateUsergroupDialog = connect<
  ICreateUsergroupDialogAppStateProps,
  ICreateUsergroupDialogDispatchProps,
  ICreateUsergroupDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: "createUsergroup",
    asyncValidate: CreateUsergroupDialogImpl.asyncValidate,
    asyncBlurFields: [CreateUsergroupDialogImpl.USERGROUP_DIALOG_FIELD_NAME], // fields that trigger validation onBlur
  })(CreateUsergroupDialogImpl) as any,
);

export { CreateUsergroupDialog };
